import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, doc, updateDoc, addDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useOrgId } from '../../context/OrgContext';

const fmt = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];
const DEFAULT_THRESHOLD = 2000; // federal 1099-NEC/MISC threshold starting tax year 2026 (OBBBA); was $600 for 2025 and earlier

const blankPayee = (name) => ({
  name, businessName: '', address: '', city: '', state: '', zip: '',
  phone: '', email: '', tin: '', tinType: 'EIN', exempt1099: true, notes: '',
});

const Form1099 = () => {
  const orgId = useOrgId();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [payees, setPayees] = useState({}); // name -> payee doc
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(CURRENT_YEAR);
  const [threshold, setThreshold] = useState(DEFAULT_THRESHOLD);

  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    const unsub = onSnapshot(collection(db, 'orgs', orgId, 'transactions'), snap => {
      setTransactions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    const unsub = onSnapshot(collection(db, 'orgs', orgId, 'payees'), snap => {
      const map = {};
      snap.docs.forEach(d => { map[d.data().name] = { id: d.id, ...d.data() }; });
      setPayees(map);
    });
    return unsub;
  }, [orgId]);

  const fromDate = `${year}-01-01`;
  const toDate   = `${year}-12-31`;

  const byPayee = {};
  transactions.forEach(t => {
    if (t.type === 'Starting Balance' || !t.payableTo) return;
    if (t.date < fromDate || t.date > toDate) return;
    if (!byPayee[t.payableTo]) byPayee[t.payableTo] = { total: 0, count: 0 };
    byPayee[t.payableTo].total += t.expense || 0;
    byPayee[t.payableTo].count += 1;
  });

  const rows = Object.entries(byPayee)
    .map(([name, v]) => ({ name, ...v, payee: payees[name] || null }))
    .filter(r => r.total >= Number(threshold || 0))
    .sort((a, b) => b.total - a.total);

  const needsFiling = rows.filter(r => r.payee ? r.payee.exempt1099 === false : false);
  const exemptCount = rows.length - needsFiling.length;

  const isComplete = (p) => !!(p && p.tin?.trim() && p.address?.trim() && p.zip?.trim());

  const toggleExempt = async (row) => {
    if (row.payee) {
      await updateDoc(doc(db, 'orgs', orgId, 'payees', row.payee.id), { exempt1099: !row.payee.exempt1099 });
    } else {
      // No profile yet — create one, flipped to "not exempt" since that's the
      // only reason you'd toggle a payee with no record.
      await addDoc(collection(db, 'orgs', orgId, 'payees'), { ...blankPayee(row.name), exempt1099: false });
    }
  };

  const editPayee = (name) => navigate(`/admin?tab=Payees&search=${encodeURIComponent(name)}`);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">1099 Prep</h2>
          <p className="text-slate-500 text-sm mt-0.5">
            Payees paid ≥ {fmt(threshold)} in {year} — federal 1099-NEC/MISC threshold is $2,000 for 2026 (was $600 through 2025)
          </p>
        </div>
        <div className="flex gap-2 no-print">
          <select
            value={year} onChange={e => setYear(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"
          >
            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <input
            type="number" value={threshold} onChange={e => setThreshold(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-28"
          />
          <button
            onClick={() => window.print()}
            className="border border-gray-300 px-3 py-1.5 rounded-lg text-sm hover:bg-gray-50 transition-colors"
          >
            Print
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 text-gray-500 text-sm">
          No payees reached {fmt(threshold)} in {year}.
        </div>
      ) : (
        <>
          <div className="rounded-xl px-4 py-3 mb-5 bg-amber-50 border border-amber-200 text-amber-800 text-sm">
            <span className="font-semibold">{needsFiling.length}</span> payee{needsFiling.length === 1 ? '' : 's'} marked "not exempt" — likely need a 1099.{' '}
            <span className="font-semibold">{exemptCount}</span> exempt or unreviewed.{' '}
            Every payee defaults to exempt until you flip it below — review each row.
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-gray-200 text-slate-600">
                  <th className="px-4 py-2.5 text-left font-semibold">Payee</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Payments</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Total Paid</th>
                  <th className="px-4 py-2.5 text-left font-semibold">1099 Status</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Info on File</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map(r => {
                  const exempt = r.payee ? !!r.payee.exempt1099 : true;
                  const complete = isComplete(r.payee);
                  return (
                    <tr key={r.name} className={!exempt ? 'bg-amber-50/40' : ''}>
                      <td className="px-4 py-2.5 font-medium text-slate-700">{r.name}</td>
                      <td className="px-4 py-2.5 text-right text-slate-600">{r.count}</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-red-600">{fmt(r.total)}</td>
                      <td className="px-4 py-2.5">
                        <label className="inline-flex items-center gap-1.5 cursor-pointer select-none no-print">
                          <input type="checkbox" checked={!exempt} onChange={() => toggleExempt(r)} className="rounded" />
                          <span className={`text-xs font-medium ${exempt ? 'text-slate-500' : 'text-amber-700'}`}>
                            {exempt ? 'Exempt' : 'Needs 1099'}
                          </span>
                        </label>
                      </td>
                      <td className="px-4 py-2.5">
                        {exempt ? (
                          <span className="text-xs text-slate-400">—</span>
                        ) : complete ? (
                          <span className="text-xs text-green-700">✓ Complete</span>
                        ) : (
                          <span className="text-xs text-red-600">⚠ Missing TIN/address</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right no-print">
                        <button onClick={() => editPayee(r.name)} className="text-xs text-blue-600 hover:underline">
                          {r.payee ? 'Edit info' : 'Add info'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default Form1099;
